import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Layout } from '@/shared/components/Layout'
import { useAuthStore } from '@/shared/store/authStore'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Badge } from '@/shared/components/ui/badge'
import { Separator } from '@/shared/components/ui/separator'
import { Trash2, Plus, Edit2, Check, X } from 'lucide-react'
import { fetchSkills, createSkill, updateSkill, deleteSkill, fetchTokenUsage } from '@/features/message-redactor/api/redactorApi'
import type { CustomSkill } from '@/features/message-redactor/types'

interface SkillFormData {
  name: string
  description: string
  systemPrompt: string
  isDefault: boolean
}

const EMPTY_FORM: SkillFormData = {
  name: '',
  description: '',
  systemPrompt: '',
  isDefault: false,
}

function SettingsPage() {
  const [skills, setSkills] = useState<CustomSkill[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState<SkillFormData>(EMPTY_FORM)
  const [tokenUsage, setTokenUsage] = useState<{ tokensUsed: number; date: string } | null>(null)

  useEffect(() => {
    // Load skills and token usage independently so a missing worker doesn't break skills
    setLoading(true)
    fetchSkills()
      .then((s) => {
        console.log('[Settings] Skills loaded', { count: s.length })
        setSkills(s)
      })
      .catch((err) => console.error('[Settings] Failed to load skills', err))
      .finally(() => setLoading(false))

    fetchTokenUsage()
      .then((u) => {
        console.log('[Settings] Token usage loaded', u)
        setTokenUsage(u)
      })
      .catch((err) => console.warn('[Settings] Token usage unavailable (worker not running?)', err))
  }, [])

  const handleCreate = async () => {
    if (!form.name || !form.systemPrompt) return
    const skill = await createSkill(form)
    setSkills((prev) => [skill, ...prev])
    setAdding(false)
    setForm(EMPTY_FORM)
  }

  const handleUpdate = async (id: string) => {
    const skill = await updateSkill(id, {
      name: form.name,
      description: form.description,
      systemPrompt: form.systemPrompt,
      isDefault: form.isDefault,
    })
    setSkills((prev) => prev.map((s) => (s.id === id ? skill : s)))
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const handleDelete = async (id: string) => {
    await deleteSkill(id)
    setSkills((prev) => prev.filter((s) => s.id !== id))
  }

  const startEdit = (skill: CustomSkill) => {
    setEditing(skill.id)
    setAdding(false)
    setForm({
      name: skill.name,
      description: skill.description ?? '',
      systemPrompt: skill.systemPrompt,
      isDefault: skill.isDefault,
    })
  }

  const cancel = () => {
    setEditing(null)
    setAdding(false)
    setForm(EMPTY_FORM)
  }

  return (
    <Layout>
      <div className="mx-auto max-w-2xl p-6 space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage skills and usage</p>
        </div>

        {/* Token Usage */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Token Usage Today</h2>
          {tokenUsage ? (
            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.min((tokenUsage.tokensUsed / 100000) * 100, 100)}%` }}
                />
              </div>
              <Badge variant="outline" className="font-mono text-xs whitespace-nowrap">
                {tokenUsage.tokensUsed.toLocaleString()} / 100,000
              </Badge>
            </div>
          ) : (
            <div className="h-8 w-full rounded bg-muted animate-pulse" />
          )}
        </section>

        <Separator />

        {/* Custom Skills */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Custom Skills</h2>
            {!adding && !editing && (
              <Button size="sm" variant="outline" onClick={() => setAdding(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add skill
              </Button>
            )}
          </div>

          {(adding || editing) && (
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <h3 className="text-sm font-medium">{adding ? 'New skill' : 'Edit skill'}</h3>
              <div className="space-y-1.5">
                <Label htmlFor="skill-name">Name</Label>
                <Input
                  id="skill-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Technical Writer"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="skill-desc">Description</Label>
                <Input
                  id="skill-desc"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of when to use this skill"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="skill-prompt">System prompt</Label>
                <Textarea
                  id="skill-prompt"
                  value={form.systemPrompt}
                  onChange={(e) => setForm((f) => ({ ...f, systemPrompt: e.target.value }))}
                  placeholder="You are a... Use {toneModifier}, {audienceContext}, {messageTypeRules} placeholders."
                  className="min-h-[120px] font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="text-xs bg-muted px-1 rounded">{'{toneModifier}'}</code>,{' '}
                  <code className="text-xs bg-muted px-1 rounded">{'{audienceContext}'}</code>,{' '}
                  <code className="text-xs bg-muted px-1 rounded">{'{messageTypeRules}'}</code> as placeholders.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => adding ? void handleCreate() : void handleUpdate(editing!)}
                  disabled={!form.name || !form.systemPrompt}
                  className="gap-2"
                >
                  <Check className="h-4 w-4" />
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancel} className="gap-2">
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 rounded-md bg-muted" />
              ))}
            </div>
          ) : skills.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No custom skills yet. Add one to override the default prompt.
            </p>
          ) : (
            <div className="space-y-2">
              {skills.map((skill) => (
                <div
                  key={skill.id}
                  className="group flex items-start justify-between rounded-md border bg-card p-3"
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{skill.name}</p>
                      {skill.isDefault && <Badge className="text-xs">Default</Badge>}
                    </div>
                    {skill.description && (
                      <p className="text-xs text-muted-foreground">{skill.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => startEdit(skill)}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => void handleDelete(skill.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  )
}

export const Route = createFileRoute('/settings/')({
  beforeLoad: () => {
    const { session, loading } = useAuthStore.getState()
    if (!loading && !session) {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsPage,
})
